import { ActionModal, FormError, FormLayout } from '@kcftech/design-system';
import {
    GroupType,
    ISystemOverviewResponse,
    ISystemResponse,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { TextInput } from 'components/forms/TextInput';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { WarningText } from 'components/text/WarningText';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useActiveIntegrations } from 'custom_hooks/data_hooks/integrations/useActiveIntegration';
import { useTreeItemIssueCount } from 'custom_hooks/data_hooks/tree/useTreeItemIssueCount';
import {
    getIntegrationText,
    getIssueCountText,
    getTreeItemDeleteActionText,
} from 'pages/workbench/side_panel/workbench_tree/modals/shared';
import { useState } from 'react';
import styled from 'styled-components';
import { extractErrorString } from 'utils/api';
import { isUndefinedOrEmpty } from 'utils/array';

const ConfirmationText = styled.div`
    margin-bottom: 0.8rem;
    font-weight: 500;
`;

export type DeleteSystemModalProps = Readonly<{
    system: ISystemOverviewResponse | ISystemResponse;
    onClose: (wasDeleted: boolean) => void;
}>;

export function DeleteSystemModal({ system, onClose }: DeleteSystemModalProps) {
    const { showToast } = useNotificationContext();
    const [systemNameInput, setSystemNameInput] = useState('');
    const [error, setError] = useState<string | null>(null);

    const { data: groupOverview, isLoading: isLoadingIssueCount } = useTreeItemIssueCount(
        system.Id,
        false,
    );
    const hasIssues =
        groupOverview?.IssueCount !== undefined && groupOverview.IssueCount > 0;

    const { data: activeIntegrations } = useActiveIntegrations(
        system.Location.LocationId,
    );
    const hasActiveIntegration = !isUndefinedOrEmpty(activeIntegrations);

    const { mutate: deleteSystem, isPending: isDeleting } = useMutation({
        mutationFn: () => sdClient.deleteSystem({ systemId: system.Id }),
        onSuccess: () => {
            showToast({
                type: 'success',
                content: `Successfully deleted ${system.Name}`,
            });
            onClose(true);
        },
        onError: (error) => {
            const errorMessage = extractErrorString(error) ?? 'Unknown error occurred';
            showToast({
                type: 'error',
                content: `Failed to delete system: ${errorMessage}`,
            });
        },
    });

    const handleDelete = () => {
        if (!systemNameInput.trim()) {
            setError('Please enter system name');
            return;
        }
        if (systemNameInput !== system.Name) {
            setError('System name does not match');
            return;
        }
        setError(null);
        deleteSystem();
    };

    return (
        <ActionModal
            title={`Delete '${system.Name}'`}
            width={680}
            height={480}
            actionType="danger"
            actionButtonText="Delete System"
            closeButtonText="Close"
            actionable={!isDeleting}
            closable={!isDeleting}
            onAction={handleDelete}
            onClose={() => onClose(false)}
        >
            <SpinnerArea isLoading={isDeleting}>
                {isLoadingIssueCount ? (
                    <div>Loading system information...</div>
                ) : (
                    <>
                        <WarningText text="This action cannot be undone." />
                        <div>Are you sure you want to delete this system?</div>
                        <ul>
                            <li>
                                {getTreeItemDeleteActionText(
                                    GroupType.System,
                                    system.IsCollectorDependentSystem,
                                )}
                            </li>
                            {!!hasIssues && (
                                <li>
                                    {getIssueCountText(
                                        groupOverview.IssueCount,
                                        GroupType.System,
                                        system.IsCollectorDependentSystem,
                                    )}
                                </li>
                            )}
                            {hasActiveIntegration && (
                                <li>
                                    {getIntegrationText(
                                        system.Location.LocationId,
                                        GroupType.System,
                                        system.IsCollectorDependentSystem,
                                    )}
                                </li>
                            )}
                        </ul>
                        <FormLayout>
                            <div>
                                <ConfirmationText>
                                    To confirm, please enter the name of the system below.
                                </ConfirmationText>
                                <TextInput
                                    value={systemNameInput}
                                    onChange={(e) => {
                                        setSystemNameInput(e.target.value);
                                        if (error) {
                                            setError(null);
                                        }
                                    }}
                                    placeholder="Enter system name"
                                    autoFocus
                                    width="100%"
                                />
                            </div>
                        </FormLayout>
                        {error && (
                            <div className="pt-10">
                                <FormError message={error} />
                            </div>
                        )}
                    </>
                )}
            </SpinnerArea>
        </ActionModal>
    );
}
